import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'components/base';
import useModalContext from 'components/context/modal';

import useEditorContext from '../context/editorContext';
import generateClassicData from '../generator/generateClassicData';
import generateYearBookData from '../generator/generateYearBookData';
import {
  getMinimumNumberOfPhotos,
  getMaximumNumberOfPhotos,
  isNumberOfPagesValid,
  getLayoutAssetUrl,
  getInitialCoverId,
  getNumberOfCoverPhotos,
} from 'editor-utils/template';
import DesignSelect from '../components/designSelect';
import useEditorRouter from '../lib/editorRouter';

import BookEditorPreview from './preview';
import PhotoWell from '../components/photoWell';
import PreviewCard from '../components/previewCard';

const BookEditor = props => {
  const editorContext = useEditorContext();
  const modalContext = useModalContext();
  const editorRouter = useEditorRouter(props.product.id);

  const { photos, pages } = props.item;
  const [coverId, setCoverId] = useState(getInitialCoverId(props.template, pages));

  const numberOfCoverPhotos = getNumberOfCoverPhotos(props.template, coverId);

  const handleOnPreviewClick = async e => {
    e.preventDefault();

    // TODO useless, when all photos are validated in the layer before
    const numberOfDamagedPhotos = photos.filter(photo => photo.alive === -1).length;
    if (numberOfDamagedPhotos > 0) {
      modalContext.toast('Please remove wrong uploaded photos.', {
        type: 'warning',
      });
      return;
    }

    if (props.config.generator === 'yearbook') {
      const [coverPhoto, ...sortedPhotos] = photos;

      const { data } = await generateYearBookData(
        props.template,
        coverPhoto,
        sortedPhotos,
        'YEARBOOK',
      );
      editorContext.setPages(props.item.id, data.pages);
    } else {
      const { data, error } = await generateClassicData(
        editorContext.accessToken,
        props.template,
        photos,
        coverId,
      );

      if (error) {
        modalContext.confirm(<>{error}</>, {
          title: 'Error creating your project',
          closeable: false,
          confirmLabel: 'OK',
        });
        return;
      }

      editorContext.setPages(props.item.id, data.pages);
    }

    editorContext.onPreviewPrints(props.product.id);
    editorRouter.push('/preview');
  };

  if (props.params[0] === 'preview' && isNumberOfPagesValid(props.template, pages)) {
    return <BookEditorPreview {...props} />;
  }

  const minimumNumberOfPhotos = getMinimumNumberOfPhotos(props.template);
  const maximumNumberOfPhotos = getMaximumNumberOfPhotos(props.template);

  return (
    <Container>
      <PhotoWell
        id={props.item.id}
        numberOfCoverPhotos={numberOfCoverPhotos}
        minimumNumberOfPhotos={minimumNumberOfPhotos}
        maximumNumberOfPhotos={maximumNumberOfPhotos}
      >
        {props.template.cover.length > 1 && (
          <DesignSelect
            title="PICK A COVER DESIGN"
            active={coverId}
            onChange={setCoverId}
            items={props.template.cover.map(page => ({
              key: page.id,
              title: page.caption,
              icon: getLayoutAssetUrl(props.template, page.id),
            }))}
            className="mb-3"
          />
        )}
        <PreviewCard
          sku={props.product.id}
          numberOfPhotos={photos.length}
          minimumNumberOfPhotos={minimumNumberOfPhotos}
          maximumNumberOfPhotos={maximumNumberOfPhotos}
          onFinish={handleOnPreviewClick}
        />
      </PhotoWell>
    </Container>
  );
};

BookEditor.propTypes = {
  item: PropTypes.object,
  params: PropTypes.arrayOf(PropTypes.string),
  config: PropTypes.object,
  product: PropTypes.object,
  template: PropTypes.object,
  onFinish: PropTypes.func,
};

export default BookEditor;
